import type { AiAssistOverviewFlag } from "./ai-assist";
import type { ClinicianRiskFlag } from "./clinician";

// TES-948 (TES-941 §RISKFLAGGOR) — the ONE list "Riskflaggor: X" on the
// patient overview, as rows. Emelie:
//   "Alla identifierade riskflaggor ska visas, oavsett var de fångats upp"
//
// A row is a form flag, an open acute alarm (TES-676, MedicalRiskFlag), or
// both: an alarm raised from a form's hard stop is the SAME finding as that
// hard stop, so it rides on the hard stop's row instead of being counted
// twice. An alarm no form flag carries (a lab alarm, a manual one, a
// submission whose flags are gone) is a row of its own.
//
// The API counts the header's "Riskflaggor: X" with this function and the
// admin renders the list with it, so the number and the length are the same
// by construction.

export interface RiskFlagRow {
  flag: AiAssistOverviewFlag | null;
  alarm: ClinicianRiskFlag | null;
}

export function riskFlagRows(
  flags: readonly AiAssistOverviewFlag[],
  alarms: readonly ClinicianRiskFlag[],
): RiskFlagRow[] {
  const alarmFor = new Map<number, ClinicianRiskFlag>();
  const orphans: ClinicianRiskFlag[] = [];
  for (const alarm of alarms) {
    const i = flags.findIndex(
      (f, idx) =>
        !alarmFor.has(idx) &&
        alarm.sourceType === "FormSubmission" &&
        f.submissionId !== undefined &&
        alarm.sourceId === f.submissionId &&
        alarm.rule === f.rule,
    );
    if (i === -1) orphans.push(alarm);
    else alarmFor.set(i, alarm);
  }
  const withAlarm: RiskFlagRow[] = [];
  const rest: RiskFlagRow[] = [];
  flags.forEach((flag, i) => {
    const alarm = alarmFor.get(i) ?? null;
    (alarm ? withAlarm : rest).push({ flag, alarm });
  });
  // Open alarms first: they are the rows with something to press.
  return [
    ...orphans.map((alarm) => ({ flag: null, alarm })),
    ...withAlarm,
    ...rest,
  ];
}

// TES-1001 (Ö-250) — acute alarms are kept apart from ordinary risk flags.
// Emelie, TES-996, 2026-09-29:
//   "Akutlarm: 1 – agera nu" / "Riskflaggor: 3"
//   "Samma signal får inte dubbelräknas. Akutlarm visas överst och följer den särskilda säkerhetsvägen."
// A-02 (2026-09-29):
//   "När en signal klassificeras som akut räknas den endast i akutlarmens antal och inte samtidigt i Riskflaggor: X."
//
// `acute` is every row that carries an OPEN alarm (TES-676; the caller
// passes uncleared alarms only): the alarm on its own, or riding on the hard
// stop it was raised from. `flags` is every other row. Each row is in exactly
// one of the two, so no finding is counted twice. A hard stop whose alarm has
// been cleared is handled and falls back to the flag list, where it is still
// visible with its original answer.
export interface SplitRiskFlagRows {
  acute: RiskFlagRow[];
  flags: RiskFlagRow[];
}

export function splitRiskFlagRows(
  flags: readonly AiAssistOverviewFlag[],
  alarms: readonly ClinicianRiskFlag[],
): SplitRiskFlagRows {
  const rows = riskFlagRows(flags, alarms);
  return {
    acute: rows.filter((r) => r.alarm !== null),
    flags: rows.filter((r) => r.alarm === null),
  };
}
